import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  AlertTriangle,
  Camera as CamIcon,
  Check,
  ChevronDown,
  Eye,
  Film,
  Loader2,
  RefreshCcw,
  ScanEye,
  Gauge,
  SlidersHorizontal,
  Users,
} from "lucide-react";
import {
  api,
  ApiError,
  type MonitoringCamera,
  type MonitoringStatus,
  type PreviewLease,
  type PreviewMode,
  type PreviewStream,
  type TransmissionLimits,
} from "../api";
import { connectHls, connectWhep, webrtcCanPlay, type StreamConnection } from "../webrtc";
import { connectDirect, directSupported, type DirectConnection } from "../direct";
import { InfoHint } from "../components/InfoHint";
import { Modal } from "../components/Modal";
import { PageHeader } from "../components/PageHeader";
import { SearchableSelect } from "../components/SearchableSelect";
import { useScope } from "../components/ScopeContext";
import { useWords } from "../words";
import { useRole } from "../hooks/useRole";
import { PlayerFullscreenButton } from "../components/PlayerFullscreenButton";
import { FisheyeOverlay, type FisheyeGeometry } from "../components/FisheyePlayer";
import { friendlyError } from "../errors";
import { isCalibrated } from "../fisheye/lens";
import { eventInside, onEscapeKey } from "../outsideDismiss";

/** Stream types, in UI order. The wire values are unchanged; only the labels
 *  speak the operator's language.
 *  - Original   (passthrough): the camera's own stream, re-muxed as-is. No
 *    quality choice — you get exactly what the camera sends. This is also the
 *    only mode where the camera's main/sub encodings differ, so that choice
 *    lives inside this button.
 *  - Re-encoded (transcode):   a clean re-encode at a chosen quality (incl.
 *    "native"), no detection overlay.
 *  - Detections (annotated):  the analysed stream with detection boxes burnt
 *    in. Cut from the analysis buffers, so it can never exceed the mux
 *    resolution (its top quality rung is labelled "Highest"). */
const MODES: Array<{ id: PreviewMode; label: string; icon: typeof Eye; hint: string }> = [
  { id: "passthrough", label: "Original", icon: Film, hint: "The camera's own stream, unmodified" },
  { id: "transcode", label: "Re-encoded", icon: Eye, hint: "Re-encoded at a chosen quality, no boxes" },
  { id: "annotated", label: "Detections", icon: ScanEye, hint: "Analysed stream with detection boxes" },
];

// Wall layouts, named by how many tiles sit across a row. Auto picks the
// count that makes each tile as large as the pane allows.
const TILE_LAYOUTS = [
  { cols: 1, label: "1 × 1" },
  { cols: 2, label: "2 × 2" },
  { cols: 3, label: "3 × 3" },
  { cols: 4, label: "4 × 4" },
  { cols: 5, label: "5 × 5" },
  { cols: 6, label: "6 × 6" },
] as const;
const TILES_PER_ROW_KEY = "bf-tiles-per-row";
const PRESERVE_ASPECT_KEY = "bf-wall-preserve-aspect";
type TileLayoutChoice = "auto" | number;

function readTileLayout(): TileLayoutChoice {
  try {
    const raw = localStorage.getItem(TILES_PER_ROW_KEY);
    if (raw === "auto" || raw == null) return "auto";
    const n = Number(raw);
    return TILE_LAYOUTS.some((l) => l.cols === n) ? n : "auto";
  } catch {
    return "auto";
  }
}

function readPreserveAspect(): boolean {
  try {
    const raw = localStorage.getItem(PRESERVE_ASPECT_KEY);
    return raw !== "0";
  } catch {
    return true;
  }
}

/** Column count that yields the largest tiles for this pane. */
function autoTileCols(
  n: number,
  paneW: number,
  paneH: number,
  preserveAspect: boolean,
): number {
  if (n <= 1) return 1;
  const maxCols = Math.min(6, n);
  if (paneW <= 0 || paneH <= 0) {
    return Math.max(1, Math.min(maxCols, Math.ceil(Math.sqrt(n))));
  }
  let best = 1;
  let bestScore = -1;
  for (let cols = 1; cols <= maxCols; cols++) {
    const rows = Math.ceil(n / cols);
    let tileW: number;
    let tileH: number;
    if (preserveAspect) {
      const aspect = (cols * 16) / (rows * 9);
      let w = paneW;
      let h = paneW / aspect;
      if (h > paneH) {
        h = paneH;
        w = paneH * aspect;
      }
      tileW = w / cols;
      tileH = h / rows;
    } else {
      tileW = paneW / cols;
      tileH = paneH / rows;
    }
    const area = tileW * tileH;
    const ratio = tileW / Math.max(tileH, 0.001);
    const closeness = 1 / (1 + Math.abs(Math.log(ratio / (16 / 9))));
    const score = preserveAspect ? area : area * closeness;
    if (score > bestScore) {
      bestScore = score;
      best = cols;
    }
  }
  return best;
}

/** A grid glyph showing the layout it selects, the way a NVR client does it. */
function TileLayoutIcon({ cols }: { cols: number }) {
  const gap = 1.1;
  const span = (16 - gap * (cols - 1)) / cols;
  const cells = [];
  for (let row = 0; row < cols; row++) {
    for (let col = 0; col < cols; col++) {
      cells.push(
        <rect
          key={`${row}-${col}`}
          x={2 + col * (span + gap)}
          y={2 + row * (span + gap)}
          width={span}
          height={span}
          rx={cols > 3 ? 0.4 : 0.8}
        />,
      );
    }
  }
  return (
    <svg width={18} height={18} viewBox="0 0 20 20" fill="currentColor" aria-hidden>
      {cells}
    </svg>
  );
}

function TileViewMenu({
  choice,
  effectiveCols,
  preserveAspect,
  onChoice,
  onPreserveAspect,
}: {
  choice: TileLayoutChoice;
  effectiveCols: number;
  preserveAspect: boolean;
  onChoice: (next: TileLayoutChoice) => void;
  onPreserveAspect: (next: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const triggerLabel = choice === "auto" ? "Auto" : `${choice} × ${choice}`;

  useEffect(() => {
    if (!open) return;
    let downOutside = false;
    const onDown = (e: PointerEvent) => {
      downOutside = !eventInside(e, ref.current);
    };
    const onUp = (e: PointerEvent) => {
      if (downOutside && !eventInside(e, ref.current)) setOpen(false);
    };
    const onKey = onEscapeKey(() => setOpen(false));
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open]);

  const itemClass = (on: boolean) =>
    "flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition " +
    (on ? "bg-bf-violet/25 text-white" : "text-bf-text hover:bg-white/5");

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Tile layout"
        title="How the cameras sit on the wall"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-bf-surface/50 px-2 py-1.5 text-sm text-bf-text transition hover:bg-white/5"
      >
        <TileLayoutIcon cols={effectiveCols} />
        <span>{triggerLabel}</span>
        <ChevronDown
          size={14}
          aria-hidden
          className={"text-bf-muted transition " + (open ? "rotate-180" : "")}
        />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute left-0 top-full z-50 mt-1 min-w-[12.5rem] overflow-hidden rounded-lg border border-white/10 bg-bf-surface p-1 shadow-xl shadow-black/50"
        >
          <button
            type="button"
            role="menuitemradio"
            aria-checked={choice === "auto"}
            onClick={() => {
              onChoice("auto");
              setOpen(false);
            }}
            className={itemClass(choice === "auto")}
          >
            <TileLayoutIcon cols={effectiveCols} />
            <span className="flex-1">Auto</span>
            {choice === "auto" && (
              <Check size={14} className="shrink-0 text-bf-violet" />
            )}
          </button>
          {TILE_LAYOUTS.map((layout) => {
            const on = choice === layout.cols;
            return (
              <button
                key={layout.cols}
                type="button"
                role="menuitemradio"
                aria-checked={on}
                onClick={() => {
                  onChoice(layout.cols);
                  setOpen(false);
                }}
                className={itemClass(on)}
              >
                <TileLayoutIcon cols={layout.cols} />
                <span className="flex-1">{layout.label}</span>
                {on && <Check size={14} className="shrink-0 text-bf-violet" />}
              </button>
            );
          })}
          <div className="my-1 border-t border-white/10" />
          <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-bf-text hover:bg-white/5">
            <input
              type="checkbox"
              className="h-3.5 w-3.5 accent-bf-violet"
              checked={preserveAspect}
              onChange={(e) => onPreserveAspect(e.target.checked)}
            />
            <span className="flex-1">Preserve aspect ratio</span>
          </label>
        </div>
      )}
    </div>
  );
}

const HEARTBEAT_MS = 10_000;
// Release the encoder seat when the tab has been hidden this long; a hidden
// tab happily heartbeats forever while nobody is watching.
const HIDDEN_RELEASE_MS = 60_000;

function useClientId(): string {
  return useMemo(() => {
    try {
      let id = localStorage.getItem("bf-client-id");
      if (!id) {
        id = crypto.randomUUID();
        localStorage.setItem("bf-client-id", id);
      }
      return id;
    } catch {
      return "";
    }
  }, []);
}

type TileState =
  | { kind: "connecting" }
  | { kind: "playing"; lease: PreviewLease }
  | { kind: "direct" } // the camera's own stream through the relay, unanalysed
  | { kind: "parked" } // tab hidden; seat released, reacquires on return
  | { kind: "refused"; message: string } // encoder pool full: manual retry
  | { kind: "live_off"; message: string }
  | { kind: "source_down"; message: string }
  | { kind: "error"; message: string };

function isSimFinished(camera: MonitoringCamera): boolean {
  if (camera.sim_finished || camera.state === "ended") return true;
  const reason = (camera.health_reason || "").toLowerCase();
  return reason.includes("simulation") && reason.includes("finished");
}

function tileStoppedMessage(camera: MonitoringCamera, liveOff?: boolean): string {
  if (liveOff || camera.sim_live === false) return "Live video is off";
  if (isSimFinished(camera)) return "This simulation has finished";
  return "";
}

/** Cameras whose listed sub stream would not start this page session. A
 *  failed start costs the gateway's whole readiness wait, so a remounted tile
 *  (enlarge, back to grid) goes straight to main instead of paying it again. */
const SUB_UNAVAILABLE = new Set<string>();

/** Original-mode paths WebRTC could not play this page session (signalling
 *  refused, or no frame ever decoded); they play over HLS from then on. */
const WHEP_UNAVAILABLE = new Set<string>();

type TileProps = {
  camera: MonitoringCamera;
  mode: PreviewMode;
  profile: string; // "wall" for grid tiles, "" for the solo view
  /** Which of the camera's own encodings to relay in Original mode. */
  stream: PreviewStream;
  nonce: number; // bump to force a fresh lease (quality change, manual retry)
  large?: boolean;
  /** Grid tiles: contain keeps the picture's shape; cover fills the card. */
  fit?: "contain" | "cover";
  /** Simulation store Live video switch is off — do not lease or retry. */
  liveOff?: boolean;
  onActivate?: () => void;
  onLease?: (lease: PreviewLease | null) => void;
};

function CameraTile({
  camera,
  mode,
  profile,
  stream,
  nonce,
  large,
  fit = "cover",
  liveOff,
  onActivate,
  onLease,
}: TileProps) {
  const { role } = useRole();
  const tileRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const directRef = useRef<DirectConnection | null>(null);
  const connRef = useRef<StreamConnection | null>(null);
  const leaseRef = useRef<PreviewLease | null>(null);
  const [state, setState] = useState<TileState>(() => {
    const stopped = tileStoppedMessage(camera, liveOff);
    return stopped
      ? { kind: "live_off", message: stopped }
      : { kind: "connecting" };
  });
  const [retryNonce, setRetryNonce] = useState(0);

  // Annotated needs detections; a view_only camera never has any.
  const effectiveMode: PreviewMode =
    mode === "annotated" && camera.role === "view_only" ? "transcode" : mode;
  // Asking for the sub stream on a camera that has none would silently hand
  // back the main picture, so fall back explicitly. A recorder can also list
  // a sub stream it will not serve right now (a fisheye streaming camera-side
  // PTZ views gives its sub encoder to the fisheye), so a sub that fails to
  // start falls back to main for this tile.
  const [subBroken, setSubBrokenState] = useState(() => SUB_UNAVAILABLE.has(camera.id));
  const setSubBroken = (broken: boolean) => {
    if (broken) SUB_UNAVAILABLE.add(camera.id);
    setSubBrokenState(broken);
  };
  const effectiveStream: PreviewStream =
    stream === "sub" && camera.has_sub_stream && !subBroken ? "sub" : "main";

  // Fisheye pictures are dewarped here, in the browser. A fisheye's own
  // stream is always the raw disk: its grid tile shows the disk, the enlarged
  // tile is a free look-around. A view cut from a fisheye is already dewarped
  // by the pipeline in the re-encoded modes; in Original mode its stream is
  // the fisheye's, so it is dewarped at the view's pose.
  const [videoEl, setVideoEl] = useState<HTMLVideoElement | null>(null);
  useEffect(() => setVideoEl(videoRef.current), []);
  const fisheye: FisheyeGeometry | null =
    camera.parent_id && camera.view && effectiveMode === "passthrough" && isCalibrated(camera.parent_lens)
      ? { lens: camera.parent_lens, view: camera.view }
      : large && isCalibrated(camera.lens)
        ? { lens: camera.lens, view: null }
        : null;

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const ac = new AbortController();
    let heartbeat: number | undefined;
    let hiddenSince: number | null = null;
    onLease?.(null);

    const release = () => {
      const lease = leaseRef.current;
      leaseRef.current = null;
      connRef.current?.close();
      connRef.current = null;
      directRef.current?.close();
      directRef.current = null;
      if (lease && lease.encoded) {
        void api.monitoring.releaseLease(camera.id, {
          mode: lease.mode,
          lease_id: lease.lease_id,
          profile: lease.encode,
        });
      }
    };

    const stopped = tileStoppedMessage(camera, liveOff);
    if (stopped) {
      video.pause();
      video.removeAttribute("src");
      video.load();
      setState({ kind: "live_off", message: stopped });
      return () => {
        ac.abort();
        release();
      };
    }

    setState({ kind: "connecting" });

    const open = async () => {
      // Cameras that are deliberately not in the pipeline (over the resolution
      // limit, or over the AI capacity limits) will never produce a picture in
      // an analysed view; say why instead of spinning on "Connecting".
      if (camera.resolution_rejected) {
        setState({
          kind: "refused",
          message:
            camera.health_reason ||
            `${camera.name} streams above its maximum allowed resolution.`,
        });
        return;
      }
      if (camera.over_capacity && effectiveMode === "annotated") {
        setState({
          kind: "refused",
          message:
            camera.health_reason ||
            `${camera.name} is not being analysed: the system is over its AI camera limit.`,
        });
        return;
      }
      if (camera.direct_live) {
        // No video processing server is online, so there is nothing to lease:
        // play the camera's own stream through the relay. Every mode shows
        // that same picture; there are no detections to draw.
        const canvas = canvasRef.current;
        if (!canvas) return;
        if (!directSupported()) {
          setState({
            kind: "refused",
            message: "This browser cannot play the camera's stream directly.",
          });
          return;
        }
        try {
          const opened = await api.streams.open(
            { camera_id: camera.id, kind: "live", stream: effectiveStream },
            ac.signal,
          );
          if (ac.signal.aborted) return;
          directRef.current = connectDirect(opened.session, canvas, {
            live: true,
            onFirstFrame: () => setState({ kind: "direct" }),
            onBehind: () => setRetryNonce((n) => n + 1),
            onError: (message) => setState({ kind: "source_down", message }),
          });
        } catch (err) {
          if (ac.signal.aborted) return;
          setState({ kind: "source_down", message: (err as Error).message });
        }
        return;
      }
      try {
        const lease = await api.monitoring.acquireLease(camera.id, {
          mode: effectiveMode,
          profile,
          stream: effectiveStream,
        });
        if (ac.signal.aborted) return;
        if (!lease.ready) {
          if (
            lease.sim_live === false ||
            /live video is off/i.test(lease.detail || "")
          ) {
            setState({ kind: "live_off", message: "Live video is off" });
            return;
          }
          if (
            lease.sim_finished ||
            /simulation has finished|simulation clip finished/i.test(
              lease.detail || "",
            )
          ) {
            setState({
              kind: "live_off",
              message: "This simulation has finished",
            });
            return;
          }
          setState({
            kind: lease.failure === "source" ? "source_down" : "error",
            message: lease.detail || "stream did not start",
          });
          return;
        }
        leaseRef.current = lease;
        onLease?.(lease);
        let conn: StreamConnection;
        if (effectiveMode !== "passthrough") {
          conn = await connectWhep(lease.path, video, ac.signal);
        } else if (
          lease.webrtc_safe !== false &&
          !WHEP_UNAVAILABLE.has(lease.path) &&
          webrtcCanPlay(lease.codec)
        ) {
          // The camera's own bitstream over WebRTC. Where the location allows
          // HLS, a signalling failure switches to it (the first-frame
          // watchdog below covers a session that connects but never decodes).
          try {
            conn = await connectWhep(lease.path, video, ac.signal);
          } catch (err) {
            if (ac.signal.aborted || !camera.hls_fallback) throw err;
            WHEP_UNAVAILABLE.add(lease.path);
            conn = connectHls(lease.path, video);
          }
        } else if (camera.hls_fallback) {
          conn = connectHls(lease.path, video);
        } else {
          release();
          setState({
            kind: "refused",
            message: `${camera.name}'s original stream can't play in this browser. Choose Re-encoded.`,
          });
          return;
        }
        if (ac.signal.aborted) {
          conn.close();
          return;
        }
        connRef.current = conn;
        setState({ kind: "playing", lease });

        if (lease.encoded) {
          heartbeat = window.setInterval(() => {
            // A hidden tab keeps timers alive; release the NVENC seat after a
            // minute and re-acquire when the operator comes back.
            if (document.hidden) {
              hiddenSince = hiddenSince ?? Date.now();
              if (Date.now() - hiddenSince > HIDDEN_RELEASE_MS) {
                window.clearInterval(heartbeat);
                release();
                setState({ kind: "parked" });
              }
              return;
            }
            hiddenSince = null;
            void api.monitoring
              .acquireLease(camera.id, {
                mode: effectiveMode,
                profile: lease.encode,
                lease_id: lease.lease_id,
                stream: effectiveStream,
              })
              .catch(() => undefined);
          }, HEARTBEAT_MS);
        }
      } catch (err) {
        if (ac.signal.aborted) return;
        if (err instanceof ApiError && err.status === 409) {
          setState({ kind: "refused", message: err.message });
        } else if (err instanceof ApiError && err.status === 503) {
          if (/live video is off/i.test(err.message)) {
            setState({ kind: "live_off", message: "Live video is off" });
          } else if (/simulation has finished|simulation clip finished/i.test(err.message)) {
            setState({
              kind: "live_off",
              message: "This simulation has finished",
            });
          } else {
            setState({ kind: "source_down", message: err.message });
          }
        } else {
          setState({ kind: "error", message: (err as Error).message });
        }
      }
    };

    const onVisible = () => {
      if (!document.hidden && leaseRef.current === null && !ac.signal.aborted) {
        setRetryNonce((n) => n + 1); // reacquire after a parked release
      }
    };
    document.addEventListener("visibilitychange", onVisible);

    // Recover tears down this camera's preview. Don't lease a source that is
    // mid-rebuild — the next recoveries bump remounts and acquire waits until
    // the new stream is actually producing video.
    if (camera.recovering) {
      return () => {
        ac.abort();
        document.removeEventListener("visibilitychange", onVisible);
        release();
      };
    }

    void open();
    return () => {
      ac.abort();
      document.removeEventListener("visibilitychange", onVisible);
      if (heartbeat) window.clearInterval(heartbeat);
      release();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    camera.id,
    liveOff,
    camera.sim_live,
    camera.sim_finished,
    camera.state,
    camera.health_reason,
    camera.resolution_rejected,
    camera.over_capacity,
    camera.direct_live,
    camera.recovering,
    camera.recoveries ?? 0,
    camera.hls_fallback,
    effectiveMode,
    effectiveStream,
    profile,
    nonce,
    retryNonce,
  ]);

  useEffect(() => {
    if (effectiveStream === "sub" && (state.kind === "error" || state.kind === "source_down")) {
      setSubBroken(true);
    }
  }, [state.kind, effectiveStream]);

  // Transient failures (camera reconnecting, gateway restarting) self-retry.
  // A store whose Live video switch is off is a chosen stop, not a flap.
  useEffect(() => {
    if (liveOff || camera.sim_live === false || isSimFinished(camera)) return;
    if (state.kind !== "error" && state.kind !== "source_down") return;
    const t = window.setTimeout(() => setRetryNonce((n) => n + 1), 5000);
    return () => window.clearTimeout(t);
  }, [state.kind, liveOff, camera.sim_live, camera.sim_finished, camera.state, camera.health_reason]);

  // Freeze watchdog: a dead source can leave the tile "playing" but frozen on
  // the last decoded frame. framesDecoded from getStats() is the only signal
  // that keeps moving exactly when the picture does (rVFC only fires for
  // composited frames; getVideoPlaybackQuality misses WebRTC in Chrome).
  useEffect(() => {
    if (state.kind !== "playing") return;
    let lastCount = -1;
    let lastProgressAt = Date.now();
    let cancelled = false;
    const iv = window.setInterval(() => {
      if (document.hidden) {
        lastProgressAt = Date.now();
        return;
      }
      const pc = connRef.current?.pc;
      if (!pc) {
        // HLS path: currentTime advancing is the equivalent liveness signal.
        const video = videoRef.current;
        if (!video) return;
        const t = video.currentTime;
        if (t !== lastCount) {
          lastCount = t;
          lastProgressAt = Date.now();
        } else if (Date.now() - lastProgressAt > 20_000) {
          if (effectiveStream === "sub" && lastCount <= 0) setSubBroken(true);
          setRetryNonce((n) => n + 1);
        }
        return;
      }
      if (pc.connectionState === "closed") return;
      pc.getStats()
        .then((stats) => {
          if (cancelled) return;
          let frames = -1;
          for (const report of stats.values()) {
            if (report.type === "inbound-rtp" && report.kind === "video") {
              frames = (report.framesDecoded as number | undefined) ?? -1;
            }
          }
          if (frames < 0) return;
          if (frames !== lastCount) {
            lastCount = frames;
            lastProgressAt = Date.now();
          } else if (frames <= 0 && effectiveMode === "passthrough") {
            // Original over WebRTC that has not decoded a frame yet. Recorders
            // send keyframes as rarely as every 24 s (measured), so wait that
            // out. Where the location allows HLS, a session that still shows
            // nothing is a bitstream this browser negotiates but cannot
            // decode: HLS for this path. Otherwise start a fresh session.
            const limit = camera.hls_fallback ? 30_000 : 45_000;
            if (Date.now() - lastProgressAt > limit) {
              const path = leaseRef.current?.path;
              if (path && camera.hls_fallback) WHEP_UNAVAILABLE.add(path);
              setRetryNonce((n) => n + 1);
            }
          } else if (Date.now() - lastProgressAt > 12_000) {
            setRetryNonce((n) => n + 1);
          }
        })
        .catch(() => undefined);
    }, 4000);
    return () => {
      cancelled = true;
      window.clearInterval(iv);
    };
  }, [state.kind, effectiveStream, effectiveMode, camera.hls_fallback]);

  const containerClass = large
    ? "relative h-full min-h-0 w-full cursor-minimize2 bg-bf-surface"
    : "relative h-full min-h-0 w-full cursor-maximize2 overflow-hidden rounded-lg border border-white/5 bg-bf-surface transition hover:border-white/20";
  const usedStream: PreviewStream | undefined =
    effectiveMode === "passthrough"
      ? state.kind === "playing"
        ? state.lease.stream ?? effectiveStream
        : effectiveStream
      : undefined;

  const dotClass =
    state.kind === "playing" || state.kind === "direct"
      ? "bg-bf-magenta"
      : state.kind === "connecting"
        ? "animate-pulse bg-amber-400"
        : state.kind === "live_off"
          ? "bg-white/30"
          : "bg-red-400";

  return (
    <div
      ref={tileRef}
      className={containerClass + " [:fullscreen]:rounded-none [:fullscreen]:bg-black"}
      onClick={() => {
        if (document.fullscreenElement) return;
        onActivate?.();
      }}
      title={large ? "Click to return to grid" : "Click to enlarge"}
    >
      <div
        className={`absolute inset-0 flex items-center justify-center bg-bf-surface ${
          camera.direct_live ? "" : "hidden"
        }`}
      >
        <canvas ref={canvasRef} />
      </div>
      <video
        ref={videoRef}
        className={
          (camera.direct_live ? "hidden " : "") + (large
            ? "h-full w-full cursor-inherit object-contain bg-bf-surface"
            : `absolute inset-0 h-full w-full cursor-inherit bg-bf-surface ${
                fit === "contain" ? "object-contain" : "object-cover"
              }`) + (fisheye ? " opacity-0" : "")
        }
        muted
        playsInline
        autoPlay
      />
      {fisheye && <FisheyeOverlay geometry={fisheye} video={videoEl} />}
      <PlayerFullscreenButton targetRef={tileRef} />
      <div className="absolute left-2 top-2 flex max-w-[calc(100%-1rem)] items-center gap-2 overflow-hidden rounded-md bg-black/60 px-2 py-1 text-xs font-medium text-white backdrop-blur">
        <span className={`inline-block h-1.5 w-1.5 shrink-0 rounded-full ${dotClass}`} />
        <span className="min-w-0 truncate" title={camera.name}>
          {camera.name}
        </span>
        {camera.role === "view_only" ? (
          <span className="shrink-0 whitespace-nowrap rounded bg-white/10 px-1 text-[10px] uppercase tracking-wide text-bf-muted">
            non-AI
          </span>
        ) : camera.analysed === false ? (
          <span
            className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded bg-amber-500/15 px-1 text-[10px] font-semibold uppercase tracking-wide text-amber-300"
            title="Not being analysed: no video processing server has this camera"
          >
            <AlertTriangle size={10} aria-label="Not being analysed" /> AI
          </span>
        ) : (
          <span className="shrink-0 whitespace-nowrap rounded bg-bf-violet/20 px-1 text-[10px] font-semibold uppercase tracking-wide text-bf-magenta">
            AI
          </span>
        )}
        {usedStream && (
          <span className="shrink-0 whitespace-nowrap rounded bg-white/10 px-1 text-[10px] uppercase tracking-wide text-bf-muted">
            {usedStream}
          </span>
        )}
      </div>
      {state.kind === "connecting" && (
        <div className="absolute inset-0 flex cursor-inherit flex-col items-center justify-center gap-2 bg-bf-surface/70 text-bf-muted">
          <Loader2 size={20} className="animate-spin" />
          <span className="text-xs">
            {camera.recovering ? "Reconnecting…" : "Connecting..."}
          </span>
        </div>
      )}
      {state.kind === "parked" && (
        <div className="absolute inset-0 flex cursor-inherit flex-col items-center justify-center gap-2 bg-bf-surface/70 text-bf-muted">
          <Eye size={20} />
          <span className="text-xs">Paused while tab was hidden</span>
        </div>
      )}
      {state.kind === "live_off" && (
        <div className="absolute inset-0 flex cursor-inherit flex-col items-center justify-center gap-2 bg-bf-surface p-3 text-center text-bf-muted">
          <Film size={20} />
          <span className="text-xs">{state.message}</span>
        </div>
      )}
      {state.kind === "refused" && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-amber-950/40 p-3 text-center text-amber-200"
          onClick={(e) => e.stopPropagation()}
        >
          <Gauge size={20} />
          <span className="text-xs">{friendlyError(state.message, role)}</span>
          <button
            onClick={() => setRetryNonce((n) => n + 1)}
            className="rounded-md border border-amber-500/40 bg-amber-500/15 px-2 py-1 text-xs font-semibold"
          >
            Retry
          </button>
        </div>
      )}
      {state.kind === "source_down" &&
        !liveOff &&
        camera.sim_live !== false &&
        !isSimFinished(camera) && (
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-red-950/40 p-3 text-center text-red-200"
          onClick={(e) => e.stopPropagation()}
        >
          <AlertCircle size={20} />
          <span className="text-xs">{friendlyError(state.message, role)}</span>
          <button
            onClick={() => {
              void api.monitoring.recover(camera.id).catch(() => undefined);
              setRetryNonce((n) => n + 1);
            }}
            className="flex items-center gap-1 rounded-md border border-red-500/40 bg-red-500/15 px-2 py-1 text-xs font-semibold"
            title="Tear down and rebuild this camera's connection now"
          >
            <RefreshCcw size={12} /> Rebuild
          </button>
        </div>
      )}
      {state.kind === "error" &&
        !liveOff &&
        camera.sim_live !== false &&
        !isSimFinished(camera) && (
        <div className="absolute inset-0 flex cursor-inherit flex-col items-center justify-center gap-2 bg-red-950/40 p-3 text-center text-red-200">
          <AlertCircle size={20} />
          <span className="text-xs">Stream interrupted; retrying</span>
        </div>
      )}
    </div>
  );
}

function TransmissionLimitsModal({
  limits,
  saving,
  onClose,
  onSave,
}: {
  limits: TransmissionLimits;
  saving: boolean;
  onClose: () => void;
  onSave: (headroom: number | null, minPerMpx: number | null) => void;
}) {
  const [headroom, setHeadroom] = useState(String(limits.reencode_headroom));
  const [minMbps, setMinMbps] = useState(
    String(limits.min_bitrate_per_mpx / 1e6),
  );
  const h = Number(headroom);
  const m = Number(minMbps);
  const valid =
    Number.isFinite(h) && h > 0 && h <= 10 && Number.isFinite(m) && m >= 0 && m <= 20;
  const isConfig =
    limits.reencode_headroom === limits.config_reencode_headroom &&
    limits.min_bitrate_per_mpx === limits.config_min_bitrate_per_mpx;

  return (
    <Modal onClose={onClose} labelledBy="limits-title">
      <h2 id="limits-title" className="text-lg font-semibold text-white">
        Transmission limits
      </h2>
      <p className="mt-2 text-sm text-bf-muted">
        Every re-encoded preview (wall tiles and solo views) is clamped against
        the bitrate the camera itself sends, so a preview never spends more
        bandwidth than the source is worth. Changes apply live and survive
        restarts.
      </p>

      <label className="mt-4 block text-sm font-medium text-bf-text">
        Bitrate cap: multiple of the camera&apos;s measured rate
        <input
          type="number"
          min="0.1"
          max="10"
          step="0.1"
          value={headroom}
          onChange={(e) => setHeadroom(e.target.value)}
          className="mt-1 w-full rounded-lg border border-white/10 bg-bf-surface/60 px-3 py-2 text-sm text-bf-text"
        />
        <span className="mt-1 block text-xs font-normal text-bf-muted">
          This re-encode has no B-frames and a much shorter GOP than the
          camera, so it needs more bits for the same picture; 2.0 is a sane
          default. Config baseline: {limits.config_reencode_headroom}x.
        </span>
      </label>

      <label className="mt-4 block text-sm font-medium text-bf-text">
        Minimum bitrate (Mb/s per megapixel of encoded output)
        <input
          type="number"
          min="0"
          max="20"
          step="0.1"
          value={minMbps}
          onChange={(e) => setMinMbps(e.target.value)}
          className="mt-1 w-full rounded-lg border border-white/10 bg-bf-surface/60 px-3 py-2 text-sm text-bf-text"
        />
        <span className="mt-1 block text-xs font-normal text-bf-muted">
          Floor under the cap, so one quiet VBR sample cannot starve an encode.
          Config baseline: {limits.config_min_bitrate_per_mpx / 1e6} Mb/s/Mpx.
        </span>
      </label>

      <div className="mt-5 flex items-center justify-between gap-2">
        <button
          onClick={() => onSave(null, null)}
          disabled={saving || isConfig}
          className="rounded-lg border border-white/10 px-3 py-2 text-sm text-bf-muted hover:text-bf-text disabled:opacity-40"
          title="Clear the overrides and follow config.yaml"
        >
          Reset to config
        </button>
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="rounded-lg border border-white/10 px-3 py-2 text-sm text-bf-muted hover:text-bf-text"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(h, Math.round(m * 1e6))}
            disabled={saving || !valid}
            className="rounded-lg bg-bf-violet/80 px-4 py-2 text-sm font-medium text-white hover:bg-bf-violet disabled:opacity-40"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </Modal>
  );
}

export default function Monitoring() {
  const qc = useQueryClient();
  const { role } = useRole();
  const clientId = useClientId();
  const sessionId = useMemo(() => crypto.randomUUID(), []);
  const { scopeParams, scopeKey, scopeLabel, scope } = useScope();
  const w = useWords();
  const [selectedCamera, setSelectedCamera] = useState<string>("all");
  // How the cameras sit on the wall. The layout and the aspect-ratio
  // choice outlive a reload.
  const [tileLayout, setTileLayout] = useState<TileLayoutChoice>(readTileLayout);
  const [preserveAspect, setPreserveAspect] = useState(readPreserveAspect);
  useEffect(() => {
    try {
      localStorage.setItem(TILES_PER_ROW_KEY, String(tileLayout));
      localStorage.setItem(PRESERVE_ASPECT_KEY, preserveAspect ? "1" : "0");
    } catch {
      /* private mode: the choice just does not persist */
    }
  }, [tileLayout, preserveAspect]);
  // Original is the default view: it is what the camera actually sends, costs
  // no GPU, and works for every camera including non-AI ones. The analysed and
  // re-encoded views are a deliberate step up from there.
  const [mode, setMode] = useState<PreviewMode>("passthrough");
  // Grid tiles use the camera's second encoding. Sixteen main-stream tiles is
  // tens of megabits off the server for pictures a few hundred pixels tall;
  // the sub stream is a fraction of that and no softer at tile size. The
  // switch is this-tab only: a new session, another store, or returning to
  // the grid starts on sub. Enlarging a tile starts on main.
  const [wallStream, setWallStream] = useState<PreviewStream>("sub");
  const [soloStream, setSoloStream] = useState<PreviewStream>("main");
  const [qualityNonce, setQualityNonce] = useState(0);
  const [soloLease, setSoloLease] = useState<PreviewLease | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [limitsOpen, setLimitsOpen] = useState(false);

  const statusQuery = useQuery<MonitoringStatus>({
    queryKey: ["monitoring", "status"],
    queryFn: api.monitoring.status,
    refetchInterval: 5000,
  });
  const camerasQuery = useQuery<MonitoringCamera[]>({
    queryKey: ["monitoring", "cameras", scopeKey],
    queryFn: () => api.monitoring.cameras(scopeParams),
    refetchInterval: (q) =>
      q.state.data?.some((c) => c.recovering) ? 1000 : 2500,
  });
  const storesQuery = useQuery({
    queryKey: ["stores"],
    queryFn: api.stores.get,
    refetchInterval: 5_000,
  });
  const liveOffStores = useMemo(
    () =>
      new Set(
        (storesQuery.data?.stores ?? [])
          .filter((s) => s.is_simulation && s.sim_live === false)
          .map((s) => s.name),
      ),
    [storesQuery.data],
  );

  const cameras = camerasQuery.data ?? [];
  const status = statusQuery.data;
  const quality = status?.quality;
  const viewers = status?.viewers;

  useEffect(() => {
    if (selectedCamera !== "all" && !cameras.some((c) => c.id === selectedCamera)) {
      setSelectedCamera("all");
    }
  }, [cameras, selectedCamera]);

  // Page-level "somebody is watching" ping: leases count encoders, not
  // people (a 16-tile wall is 16 leases, one person).
  useEffect(() => {
    const view = selectedCamera === "all" ? "wall" : `solo:${selectedCamera}`;
    const ping = () => {
      if (document.hidden) return;
      void api.monitoring
        .session({ session_id: sessionId, client_id: clientId, view })
        .catch(() => undefined);
    };
    ping();
    const iv = window.setInterval(ping, HEARTBEAT_MS);
    return () => window.clearInterval(iv);
  }, [sessionId, clientId, selectedCamera]);

  const setGlobalQuality = useMutation({
    mutationFn: (q: string) => api.monitoring.setGlobalQuality(q),
    onError: (err: Error) => setErrorMsg(err.message),
    onSuccess: async () => {
      setErrorMsg(null);
      await qc.invalidateQueries({ queryKey: ["monitoring", "status"] });
      setQualityNonce((n) => n + 1); // re-acquire leases at the new quality
    },
  });
  const setCameraQuality = useMutation({
    mutationFn: (args: { id: string; q: string }) =>
      api.monitoring.setCameraQuality(args.id, args.q),
    onError: (err: Error) => setErrorMsg(err.message),
    onSuccess: async () => {
      setErrorMsg(null);
      await qc.invalidateQueries({ queryKey: ["monitoring"] });
      setQualityNonce((n) => n + 1);
    },
  });
  const setWallProfile = useMutation({
    mutationFn: (p: { height: number; bitrate: number }) =>
      api.monitoring.setWallProfile(p.height, p.bitrate),
    onError: (err: Error) => setErrorMsg(err.message),
    onSuccess: async () => {
      setErrorMsg(null);
      await qc.invalidateQueries({ queryKey: ["monitoring", "status"] });
      setQualityNonce((n) => n + 1); // grid tiles re-lease at the new profile
    },
  });
  const setLimits = useMutation({
    mutationFn: (b: {
      reencode_headroom: number | null;
      min_bitrate_per_mpx: number | null;
    }) => api.monitoring.setTransmissionLimits(b),
    onError: (err: Error) => setErrorMsg(err.message),
    onSuccess: async () => {
      setErrorMsg(null);
      setLimitsOpen(false);
      await qc.invalidateQueries({ queryKey: ["monitoring", "status"] });
      setQualityNonce((n) => n + 1); // re-lease so the new clamps apply now
    },
  });

  const wallRef = useRef<HTMLDivElement>(null);
  const [pane, setPane] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = wallRef.current;
    if (!el) return;
    const measure = () => setPane({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [selectedCamera, cameras.length]);

  const gridShape = useMemo(() => {
    const n = cameras.length || 1;
    const cols =
      tileLayout === "auto"
        ? autoTileCols(n, pane.w, pane.h, preserveAspect)
        : Math.min(tileLayout, n);
    return { cols, rows: Math.ceil(n / cols) };
  }, [cameras.length, tileLayout, pane.w, pane.h, preserveAspect]);

  const soloCam = cameras.find((c) => c.id === selectedCamera);
  const activeStream: PreviewStream =
    selectedCamera === "all" ? wallStream : soloStream;
  // Non-AI (view-only) cameras never traverse the detection pipeline, so a
  // "Detections" stream does not exist for them — offer only Original/Formatted.
  const soloIsViewOnly = soloCam?.role === "view_only";
  useEffect(() => {
    if (soloIsViewOnly && mode === "annotated") setMode("transcode");
  }, [soloIsViewOnly, mode]);

  // Only the original stream has two variants, and only where the recorder
  // provides one. The grid returns to sub whenever that choice exists again
  // (new session, store change, back from a single camera). Main on the wall
  // lasts only until you leave the grid — it is never written to storage.
  const gridHasSub = cameras.some((c) => c.has_sub_stream);
  const subAvailable =
    selectedCamera === "all" ? gridHasSub : Boolean(soloCam?.has_sub_stream);
  useEffect(() => {
    if (selectedCamera !== "all") return;
    if (gridHasSub) setWallStream("sub");
    else if (cameras.length) setWallStream("main");
  }, [selectedCamera, gridHasSub, cameras.length]);
  useEffect(() => {
    if (selectedCamera === "all" || !cameras.length) return;
    if (!subAvailable && soloStream !== "main") setSoloStream("main");
  }, [cameras.length, selectedCamera, subAvailable, soloStream]);
  const presetNames = Object.keys(quality?.presets ?? {});

  // "Detections" is cut from the analysis (mux) buffers, so it can never be
  // sharper than the mux. Cap its quality menu at the mux height and rename
  // the top rung ("native" / keep-source) to "Highest".
  const isDetections = mode === "annotated";
  const muxHeight = status?.mux?.height ?? 0;
  const presetLabel = (name: string): string =>
    isDetections && name === "native" ? "Highest" : name;

  /**
   * Why a rung cannot be used in the current view, or "" when it can.
   *
   * Detections is drawn on the analysis buffers, so it can never be sharper
   * than the mux — but the rung is DISABLED WITH A REASON rather than removed.
   * Silently dropping 4K is what made it look like the option did not exist
   * (Detections is the default view, so the taller rungs were missing the
   * moment the page opened, on every camera).
   */
  const presetBlockedReason = (name: string): string => {
    if (!isDetections) return "";
    const p = quality?.presets?.[name];
    if (!p || p.height === 0) return "";
    if (muxHeight > 0 && p.height > muxHeight) {
      return `Detections are drawn at ${muxHeight}p, so ${name} would only upscale. Use the Formatted view for higher quality.`;
    }
    return "";
  };
  const presetOptions = useMemo(
    () =>
      presetNames
        .map((name) => ({ name, preset: quality?.presets?.[name] }))
        .sort((a, b) => (a.preset?.height ?? 0) - (b.preset?.height ?? 0))
        .map(({ name, preset }) => {
          const blocked = presetBlockedReason(name);
          return {
            value: name,
            label: presetLabel(name),
            hint:
              blocked ||
              (preset && preset.height > 0
                ? `${preset.height}p · ${(preset.bitrate / 1e6).toFixed(1)} Mb/s`
                : "Match the camera"),
            disabled: Boolean(blocked),
          };
        }),
    // presetLabel / presetBlockedReason close over mode + mux, both listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [presetNames.join(","), quality, isDetections, muxHeight],
  );

  // Wall-profile choices reuse the sized quality presets (native/0 makes no
  // sense for a 16-tile grid). The current profile may have been set in
  // config.yaml to values matching no preset; show it as-is then.
  const wall = status?.wall_profile;
  const wallOptions = useMemo(
    () =>
      Object.entries(quality?.presets ?? {})
        .filter(([, p]) => p.height > 0)
        .sort((a, b) => a[1].height - b[1].height),
    [quality],
  );
  const wallValue =
    wallOptions.find(
      ([, p]) => p.height === wall?.height && p.bitrate === wall?.bitrate,
    )?.[0] ?? "custom";

  const renderTiles = () => {
    if (cameras.length === 0) {
      return (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 rounded-xl border border-white/5 bg-bf-surface/30 text-center text-bf-muted">
          <CamIcon size={32} />
          <p className="text-sm">
            {scope.kind === "all"
              ? "No enabled cameras. Add one in Settings."
              : `No enabled cameras in ${scopeLabel}.`}
          </p>
          {scope.kind !== "all" && (
            <p className="max-w-sm text-xs">
              Switch to another {w.store} or {w.group} using the picker at the
              top of the page, or add cameras to this one in Settings.
            </p>
          )}
        </div>
      );
    }

    if (selectedCamera === "all") {
      const wallAspect =
        (gridShape.cols * 16) / (gridShape.rows * 9);
      const tiles = cameras.map((cam) => (
        <CameraTile
          key={cam.id}
          camera={cam}
          mode={mode}
          profile="wall"
          stream={wallStream}
          nonce={qualityNonce}
          fit={preserveAspect ? "contain" : "cover"}
          liveOff={
            cam.sim_live === false || liveOffStores.has(cam.store || "")
          }
          onActivate={() => {
            setSoloStream("main");
            setSelectedCamera(cam.id);
          }}
        />
      ));
      return (
        <div
          ref={wallRef}
          className="flex min-h-0 w-full flex-1 items-center justify-center [container-type:size]"
        >
          {preserveAspect ? (
            <div
              className="grid gap-2"
              style={{
                width: `min(100cqw, calc(100cqh * ${wallAspect}))`,
                height: `min(100cqh, calc(100cqw / ${wallAspect}))`,
                gridTemplateColumns: `repeat(${gridShape.cols}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${gridShape.rows}, minmax(0, 1fr))`,
              }}
            >
              {tiles}
            </div>
          ) : (
            <div
              className="grid h-full min-h-0 w-full gap-2"
              style={{
                gridTemplateColumns: `repeat(${gridShape.cols}, minmax(0, 1fr))`,
                gridTemplateRows: `repeat(${gridShape.rows}, minmax(0, 1fr))`,
              }}
            >
              {tiles}
            </div>
          )}
        </div>
      );
    }

    if (!soloCam) return null;
    const showLeaseMeta =
      Boolean(soloLease?.encoded) ||
      Boolean(soloLease && !soloLease.encoded && role === "dev") ||
      Boolean(!soloCam.healthy && soloCam.health_reason);
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden rounded-xl border border-white/5 bg-bf-surface/40 shadow-2xl">
        <div className="min-h-[12rem] min-w-0 flex-1">
          <CameraTile
            camera={soloCam}
            mode={mode}
            profile=""
            stream={soloStream}
            nonce={qualityNonce}
            liveOff={
              soloCam.sim_live === false ||
              liveOffStores.has(soloCam.store || "")
            }
            onActivate={() => setSelectedCamera("all")}
            onLease={setSoloLease}
            large
          />
        </div>
        {showLeaseMeta && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2 text-xs text-bf-muted">
            {soloLease?.encoded && (
              <span>
                {soloLease.resolution}
                {role === "dev" && soloLease.bitrate
                  ? ` · ${(soloLease.bitrate / 1e6).toFixed(1)} Mb/s`
                  : ""}
                {role === "dev" && soloLease.encode ? ` · ${soloLease.encode}` : ""}
              </span>
            )}
            {soloLease?.encoded && soloLease.note && (
              <span className="text-amber-300/80">
                {friendlyError(soloLease.note, role)}
              </span>
            )}
            {!soloLease?.encoded && soloLease && role === "dev" && (
              <span>Original bitstream (re-muxed, no re-encode)</span>
            )}
            {!soloCam.healthy && soloCam.health_reason && (
              <span className="text-red-300/90">
                {friendlyError(soloCam.health_reason, role)}
              </span>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <PageHeader
        className="shrink-0"
        title="Live view"
        description="Watch your cameras live. Overlay detections if you prefer."
        actions={
          /* Viewers and encoders are separate measurements, both read from the
             pipeline: one person on a 16-tile wall is one viewer holding
             sixteen encoders, so neither number can be derived from the other. */
          <div className="flex flex-wrap items-center gap-2">
            {viewers && (
              <div className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-bf-surface/50 px-3 py-2 text-xs text-bf-muted">
                <Users size={14} />
                Viewers {viewers.sessions}
                <InfoHint>
                  {viewers.sessions} viewer page
                  {viewers.sessions === 1 ? "" : "s"} open on{" "}
                  {viewers.distinct_clients} device
                  {viewers.distinct_clients === 1 ? "" : "s"}, holding{" "}
                  {viewers.lease_holders} encoder seat
                  {viewers.lease_holders === 1 ? "" : "s"}. One person watching a
                  grid is a single viewer holding one seat per tile.
                </InfoHint>
              </div>
            )}
            {status?.encoders && (
              <div className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-bf-surface/50 px-3 py-2 text-xs text-bf-muted">
                <Gauge size={14} />
                Encoders {status.encoders.active}/{status.encoders.max}
                <InfoHint>
                  Re-encoded streams running right now, against the maximum this
                  hardware supports. The Original view needs none of them.
                </InfoHint>
              </div>
            )}
          </div>
        }
      />

      <div className="flex shrink-0 flex-wrap items-center gap-3 rounded-xl border border-white/5 bg-bf-surface/30 p-3 shadow-sm">
        <div className="flex items-center gap-2">
          <label htmlFor="camera-select" className="text-sm font-medium text-bf-muted">
            Camera:
          </label>
          <SearchableSelect
            id="camera-select"
            className="w-60"
            ariaLabel="Select camera"
            value={selectedCamera}
            onChange={(id) => {
              if (id !== "all") setSoloStream("main");
              setSelectedCamera(id);
            }}
            options={[
              { value: "all", label: "All cameras (grid)" },
              ...cameras.map((cam) => ({
                value: cam.id,
                label: cam.name,
                hint: cam.store || undefined,
                keywords: cam.id,
                badge: cam.role === "view_only" ? "Non-AI" : "AI",
                badgeClass:
                  cam.role === "view_only"
                    ? undefined
                    : "bg-bf-violet/20 font-semibold text-bf-magenta",
              })),
            ]}
          />
        </div>

        {selectedCamera === "all" && (
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-bf-muted">View:</span>
            <TileViewMenu
              choice={tileLayout}
              effectiveCols={gridShape.cols}
              preserveAspect={preserveAspect}
              onChoice={setTileLayout}
              onPreserveAspect={setPreserveAspect}
            />
          </div>
        )}

        <div className="h-6 w-px bg-white/10" />

        <label className="text-sm font-medium text-bf-muted">Stream:</label>
        <InfoHint>
          <b>Original</b> relays exactly what the camera sends — no GPU cost, no
          quality choice. <b>Re-encoded</b> re-encodes it at a quality you pick.{" "}
          <b>Detections</b> re-encodes the analysed picture with boxes drawn on,
          so it is limited to the analysis resolution.
        </InfoHint>
        <div className="flex items-center gap-1 rounded-lg border border-white/10 bg-bf-surface/60 p-1">
          {MODES.map((m) => {
            const Icon = m.icon;
            const active = mode === m.id;
            const disabled = m.id === "annotated" && soloIsViewOnly;
            // Main vs sub belongs to the ORIGINAL stream and nothing else: the
            // re-encoded modes are cut from the decoded main stream. Nesting
            // the choice inside this segment says that without a second label
            // somewhere else in the toolbar claiming to be a separate setting.
            const withStreamChoice = m.id === "passthrough" && subAvailable;
            return (
              <div
                key={m.id}
                className={`flex items-center rounded-md transition ${
                  active ? "bg-bf-violet/30" : ""
                }`}
              >
                <button
                  onClick={() => !disabled && setMode(m.id)}
                  disabled={disabled}
                  title={
                    disabled
                      ? "This is a Non-AI camera — it isn't analysed, so there is no Detections stream."
                      : m.hint
                  }
                  className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition disabled:cursor-not-allowed disabled:opacity-40 ${
                    active ? "text-white" : "text-bf-muted hover:bg-white/5 hover:text-bf-text"
                  }`}
                >
                  <Icon size={14} /> {m.label}
                </button>
                {withStreamChoice && (
                  <span className="flex items-center gap-0.5 pr-1.5">
                    {(["main", "sub"] as const).map((s) => (
                      <button
                        key={s}
                        onClick={() => {
                          setMode("passthrough");
                          if (selectedCamera === "all") setWallStream(s);
                          else setSoloStream(s);
                        }}
                        title={
                          s === "main"
                            ? "The camera's full-quality encoding. On a full wall this is tens of megabits off the server for tile-sized pictures."
                            : "The camera's second, lower-bitrate encoding — far lighter on the network and no softer at tile size"
                        }
                        className={`flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium uppercase tracking-wide transition ${
                          active && activeStream === s
                            ? "bg-white/20 text-white"
                            : "text-bf-muted hover:bg-white/10 hover:text-bf-text"
                        }`}
                      >
                        {s}
                        {s === "main" && (
                          <AlertTriangle
                            size={11}
                            className="text-brand-pink"
                            aria-label="Heavy on the network across a full wall"
                          />
                        )}
                      </button>
                    ))}
                  </span>
                )}
                {m.id === "annotated" && (
                  <span className="pr-1.5">
                    <InfoHint>
                      Detections is only available for AI cameras. A Non-AI
                      camera is relayed but never analysed, so there are no
                      boxes to draw on it — use Original or Re-encoded for
                      those.
                    </InfoHint>
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {quality && mode !== "passthrough" && selectedCamera === "all" && wall && (
          <>
            <div className="h-6 w-px bg-white/10" />
            <div className="flex items-center gap-2">
              <label htmlFor="wall-select" className="text-sm font-medium text-bf-muted">
                Tiles quality:
              </label>
              <SearchableSelect
                id="wall-select"
                className="w-48"
                ariaLabel="Grid tile quality"
                value={wallValue}
                onChange={(name) => {
                  const p = quality.presets[name];
                  if (p) setWallProfile.mutate({ height: p.height, bitrate: p.bitrate });
                }}
                disabled={setWallProfile.isPending}
                title="Per-tile encode for the grid. Applies live: tiles reconnect at the new quality."
                searchThreshold={99}
                options={[
                  ...(wallValue === "custom"
                    ? [
                        {
                          value: "custom",
                          label: `${wall.height ? `${wall.height}p` : "native"} · ${(wall.bitrate / 1e6).toFixed(1)} Mb/s`,
                        },
                      ]
                    : []),
                  ...wallOptions.map(([name, p]) => {
                    const blocked = presetBlockedReason(name);
                    return {
                      value: name,
                      label: `${name} · ${(p.bitrate / 1e6).toFixed(1)} Mb/s`,
                      hint: blocked || undefined,
                      disabled: Boolean(blocked),
                    };
                  }),
                ]}
              />
              {cameras.length > 1 && (
                <span
                  className="text-xs text-bf-muted"
                  title="Worst-case grid bandwidth: per-tile bitrate x cameras. Tiles of low-bitrate cameras are clamped lower (see Limits)."
                >
                  ≤ {((wall.bitrate * cameras.length) / 1e6).toFixed(0)} Mb/s total
                </span>
              )}
            </div>
          </>
        )}

        {quality && mode !== "passthrough" && soloCam && (
          <>
            <div className="h-6 w-px bg-white/10" />
            <div className="flex items-center gap-2">
              <label htmlFor="quality-select" className="text-sm font-medium text-bf-muted">
                Quality:
              </label>
              <SearchableSelect
                id="quality-select"
                className="w-40"
                ariaLabel="Default quality"
                value={quality.default}
                onChange={(v) => setGlobalQuality.mutate(v)}
                disabled={setGlobalQuality.isPending}
                title="Fleet default for the solo view; changes apply live on the next connect."
                searchThreshold={99}
                options={presetOptions}
              />
              <label htmlFor="camera-quality" className="text-sm font-medium text-bf-muted">
                This camera:
              </label>
              <SearchableSelect
                id="camera-quality"
                className="w-44"
                ariaLabel="This camera quality"
                value={quality.per_source[soloCam.id] ?? ""}
                onChange={(v) => setCameraQuality.mutate({ id: soloCam.id, q: v })}
                disabled={setCameraQuality.isPending}
                title="Per-camera override; empty follows the fleet default"
                searchThreshold={99}
                options={[
                  {
                    value: "",
                    label: `Default (${presetLabel(quality.default)})`,
                    hint: "Follow the fleet default",
                  },
                  ...presetOptions,
                ]}
              />
              {soloCam.max_height ? (
                <span
                  className="text-xs text-bf-muted"
                  title="Configured in Settings. A camera streaming above its limit is refused rather than ingested."
                >
                  Source limit {soloCam.max_height}p
                </span>
              ) : null}
            </div>
          </>
        )}

        {status?.limits && mode !== "passthrough" && (
          <>
            <div className="h-6 w-px bg-white/10" />
            <button
              onClick={() => setLimitsOpen(true)}
              title="Transmission limits: bitrate clamps applied to every re-encoded preview"
              className="flex items-center gap-1.5 rounded-lg border border-white/10 bg-bf-surface/60 px-3 py-2 text-sm font-medium text-bf-muted transition hover:bg-white/5 hover:text-bf-text"
            >
              <SlidersHorizontal size={14} /> Limits
            </button>
          </>
        )}
      </div>

      {limitsOpen && status?.limits && (
        <TransmissionLimitsModal
          limits={status.limits}
          saving={setLimits.isPending}
          onClose={() => setLimitsOpen(false)}
          onSave={(headroom, minPerMpx) =>
            setLimits.mutate({
              reencode_headroom: headroom,
              min_bitrate_per_mpx: minPerMpx,
            })
          }
        />
      )}

      {status &&
        status.deepstream !== "ready" &&
        !camerasQuery.data?.some((c) => c.direct_live) && (
          <div className="shrink-0 rounded-xl border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-200">
            {`The analysis pipeline is starting up (${status.deepstream}). Original previews still work; encoded views connect as soon as it is ready.`}
          </div>
        )}
      {errorMsg && (
        <div className="shrink-0 rounded-xl border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-300">
          {friendlyError(errorMsg, role)}
        </div>
      )}

      {statusQuery.isLoading || camerasQuery.isLoading ? (
        <div className="flex min-h-0 flex-1 items-center justify-center rounded-xl border border-white/5 bg-bf-surface/30">
          <Loader2 size={32} className="animate-spin text-bf-muted" />
        </div>
      ) : (
        renderTiles()
      )}
    </div>
  );
}
